import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {EWarningBox} from '@ELib/components/EBox';
import {ECancelButton, ESubmitButton} from '@ELib/components/EButtons';
import {ETextPrimary} from '@ELib/components/ETextPrimary';
import {EForm, EFormButtonRow} from '@ELib/components/form/EForm';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';

import {ContentWrapper} from '../sharedStyles/login';

import {OTPStep} from './OTPStep';
import {PasswordStep} from './PasswordStep';
import {UsernameStep} from './UsernameStep';
import {
  abortLogin,
  abortPasswordReset,
  initResetWithOTP,
  setLocalBaseURL,
  setNewPassword,
  validateOTP,
} from './resetLogic';
import {LogicReturnType, PasswordResetForm, ResetError} from './types';

type Props = {
  onLoad?: () => void;
  onSuccess?: (data?: unknown) => void;
  onAbort: () => void;
  baseURL?: string;
  overrideUsername?: string;
};

enum Step {
  USERNAME = 0,
  OTP = 1,
  PASSWORD = 2,
}

export const PasswordReset = ({
  onLoad,
  onSuccess,
  onAbort,
  overrideUsername,
  baseURL,
}: Props): React.ReactElement => {
  const [error, setError] = useState<string | undefined>();
  const [step, setStep] = useState<Step>(Step.USERNAME);

  const [form, setForm] = useState<PasswordResetForm>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUsername = urlParams.get('username');
    return {username: overrideUsername || urlUsername || ''};
  });
  const [isLoading, setIsLoading] = useState(false);

  const {t} = useTranslation('lib');

  if (baseURL) {
    setLocalBaseURL(baseURL);
  }

  const abortReset = useCallback(() => {
    abortPasswordReset();
    onAbort();
  }, [onAbort]);

  const currentStepComplete = useMemo(() => {
    switch (step) {
      case Step.USERNAME:
        return Boolean(form.username);
      case Step.OTP:
        return Boolean(form.otp);
      case Step.PASSWORD:
        return Boolean(form.username) && Boolean(form.password);
      default:
        return false;
    }
  }, [form.otp, form.password, form.username, step]);

  const requestHandler = useCallback(
    (reqFunc: (form: PasswordResetForm) => LogicReturnType) => async () => {
      setIsLoading(true);

      try {
        const logicError = await reqFunc(form);
        if (logicError) {
          setError(t(logicError));
        } else {
          if (step === Step.PASSWORD) {
            onSuccess?.();
            return;
          }

          setStep(step + 1);
          setError(undefined);
        }
      } catch {
        setError(t(ResetError.UNEXPECTED));
      }
      setIsLoading(false);
    },
    [form, onSuccess, step, t]
  );

  const {text, buttonText, action, CurrentStep} = useMemo((): {
    text: undefined | string;
    buttonText: string;
    action: () => Promise<void>;
    CurrentStep: ({
      form,
      setForm,
    }: {
      form: PasswordResetForm;
      setForm: (form: PasswordResetForm) => void;
      setError: (err: string) => void;
    }) => React.ReactElement;
  } => {
    switch (step) {
      case Step.USERNAME:
        return {
          text: t('login.passwordReset.text'),
          buttonText: t('login.actions.getOneTimePassword'),
          action: requestHandler(initResetWithOTP),
          CurrentStep: UsernameStep,
        };
      case Step.OTP:
        return {
          text: t('login.passwordReset.otpText'),
          buttonText: t('login.actions.continueToReset'),
          action: requestHandler(validateOTP),
          CurrentStep: OTPStep,
        };
      case Step.PASSWORD:
        return {
          text: undefined,
          buttonText: t('login.actions.setPassword'),
          action: requestHandler(setNewPassword),
          CurrentStep: PasswordStep,
        };
      default:
        return {
          text: undefined,
          buttonText: '',
          action: () => Promise.resolve(),
          CurrentStep: () => <></>,
        };
    }
  }, [step, t, requestHandler]);

  useMountEffect(() => {
    onLoad?.();
    abortLogin();
  });

  return (
    <ContentWrapper>
      {text ? <ETextPrimary>{text}</ETextPrimary> : null}
      <EForm
        onSubmit={() => {
          action();
        }}
      >
        <CurrentStep form={form} setError={setError} setForm={setForm} />
        {error && <EWarningBox>{error}</EWarningBox>}
        <EFormButtonRow>
          <ECancelButton onClick={abortReset} />
          <ESubmitButton
            disabled={isLoading || !currentStepComplete}
            isSubmitting={isLoading}
          >
            {buttonText}
          </ESubmitButton>
        </EFormButtonRow>
      </EForm>
    </ContentWrapper>
  );
};
